import { Alert, Checkbox, Col, FormGroup, Row, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IAfwijkingen } from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1DiagnosticReportCustomControlData';
import { InputContainer } from '@frontend/components/elements/forms';
import { FormValidationMessage } from '@frontend/components/elements/FormValidationMessage';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { IDiagnosticReportAfwijkingenAddEditProps } from './DiagnosticReportAfwijkingenAddEdit';
import { AfwijkingenCheckboxGroupRow } from './AfwijkingenCheckboxGroupRow';

const afwijkingenWithOsAndOd = [
    { key: 'cataract', label: 'Cataract' },
    { key: 'glaucoom', label: 'Glaucoom' },
    { key: 'pseudofaak', label: 'Pseudofaak' },
    { key: 'md_nat', label: 'MD nat' },
    { key: 'md_droog', label: 'MD droog' },
    { key: 'drp', label: 'DRP' },
    { key: 'amblyopie', label: 'Amblyopie' },
];
const padding = { paddingBottom: '7px' };

export function AfwijkingenAddEdit(props: IDiagnosticReportAfwijkingenAddEditProps) {
    const {
        errors,
        customControlData: { afwijkingen, toelichting_verworven_nystagmus },
        handleChangeCustomControl,
        validateOneCustomControl,
    } = props;

    const [isAndersChecked, setIsAndersChecked] = useState(!!afwijkingen.anders_text);
    const [hasAndersTextError, setHasAndersTextError] = useState(false);
    const [hasBevindingenError, setHasBevindingenError] = useState(false);

    useEffect(() => {
        setHasBevindingenError(
            errors.has('diagnosticReport.customControlData.afwijkingen') || errors.has('customControlData.afwijkingen')
        );
    }, [errors]);

    useEffect(() => {
        setHasAndersTextError(isAndersChecked && !afwijkingen.anders_text);
    }, [isAndersChecked, afwijkingen.anders_text]);

    useEffect(() => {
        if (!afwijkingen.verworven_nystagmus) {
            handleChangeCustomControl({ toelichting_verworven_nystagmus: null });
        }
    }, [afwijkingen.verworven_nystagmus]);

    return (
        <>
            <FormGroup
                hasErrors={
                    errors.has('diagnosticReport.customControlData.afwijkingen') ||
                    errors.has('customControlData.afwijkingen') ||
                    hasAndersTextError
                }
                isRequired={true}
                label="Afwijkingen"
            >
                <InputContainer className="auto">
                    {afwijkingenWithOsAndOd.map((item, index) => (
                        <AfwijkingenCheckboxGroupRow
                            afwijkingen={afwijkingen}
                            handleChangeAfwijkingen={handleChangeAfwijkingen}
                            key={index}
                            label={item.label}
                            overarchingKey={item.key}
                            style={padding}
                        />
                    ))}
                    <FormGroup label="Nystagmus">
                        <Row style={padding}>
                            <Col span={9}>
                                <Checkbox
                                    checked={afwijkingen.aangeboren_nystagmus}
                                    className="automation-nystagmus-aangeboren"
                                    label="Aangeboren"
                                    onChange={async (e) => {
                                        await handleChangeAfwijkingen('aangeboren_nystagmus', e.target.checked);
                                    }}
                                />
                                <div style={{ paddingTop: '7px' }}>
                                    <Checkbox
                                        checked={afwijkingen.verworven_nystagmus}
                                        className="automation-nystagmus-verworven"
                                        label="Verworven"
                                        onChange={async (e) => {
                                            await handleChangeAfwijkingen('verworven_nystagmus', e.target.checked);
                                        }}
                                    />
                                    {afwijkingen.verworven_nystagmus && (
                                        <div style={{ marginLeft: '34px' }}>
                                            <TextInput
                                                className="automation-nystagmus-verworven-toelichting"
                                                disabled={!afwijkingen.verworven_nystagmus}
                                                error={getValidationErrorTranslation(
                                                    errors.get(
                                                        'diagnosticReport.customControlData.toelichting_verworven_nystagmus'
                                                    ) || errors.get('customControlData.toelichting_verworven_nystagmus')
                                                )}
                                                onBlur={async (e) => {
                                                    await validateOneCustomControl('toelichting_verworven_nystagmus');
                                                }}
                                                onChange={async (e) => {
                                                    await handleChangeCustomControl({
                                                        toelichting_verworven_nystagmus: e.target.value
                                                            ? e.target.value
                                                            : null,
                                                    });
                                                }}
                                                placeholder="Toelichting verworven nystagmus..."
                                                value={toelichting_verworven_nystagmus || ''}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup
                        hasErrors={hasAndersTextError}
                        label="Overig"
                    >
                        <Row>
                            <Col span={9}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        checked={isAndersChecked}
                                        className="automation-afwijkingen-overig"
                                        onChange={async (event) => await onChangeAndersCheckbox(event.target.checked)}
                                    />

                                    <TextInput
                                        className="automation-afwijkingen-overig-text"
                                        onChange={async (e) => {
                                            await handleChangeAfwijkingen(
                                                'anders_text',
                                                e.target.value ? e.target.value : null
                                            );
                                        }}
                                        placeholder="Anders, namelijk..."
                                        style={{ marginLeft: '12px' }}
                                        value={afwijkingen.anders_text || ''}
                                    />
                                </div>
                                <div style={{ marginLeft: '32px' }}>
                                    <FormValidationMessage error={hasAndersTextError ? 'string_empty' : ''} />
                                </div>
                            </Col>
                        </Row>
                    </FormGroup>
                </InputContainer>
            </FormGroup>
            {hasBevindingenError && !hasAndersTextError && (
                <Alert appearance="warning">Kies minstens één van de afwijkingen uit de lijst</Alert>
            )}
        </>
    );

    async function handleChangeAfwijkingen(
        key: keyof IAfwijkingen,
        value: IAfwijkingen[keyof IAfwijkingen]
    ): Promise<void> {
        const newAfwijkingen = {
            ...afwijkingen,
            [key]: value,
        };

        await handleChangeCustomControl({
            afwijkingen: newAfwijkingen,
        });

        validateOneCustomControl('afwijkingen');
    }

    async function onChangeAndersCheckbox(value: boolean): Promise<void> {
        setIsAndersChecked(value);
        if (!value) {
            await handleChangeAfwijkingen('anders_text', null);
        }
    }
}
